// İSG uzmanı portalı — Ziyaret geçmişi / rapor (api-contract §11, tenant-scoped). Tüm
// durumlar = `GET /visits`; filtreler: durum (OnSite/CheckedOut), saha, şube, tarih
// aralığı (from/to — check-in, UTC ISO §4), arama (ziyaretçi ad/telefon). UTable + paging
// (total/filteredTotal). Satır → ziyaret detayı (salt-okunur modal). Sort GÖNDERİLMEZ
// (backend check-in azalan). Saha/şube seçenekleri Ayarlar §10 uçlarından (USelect reuse).

import {
  Button,
  Card,
  CardContent,
  EmptyState,
  Field,
  Input,
  Pagination,
  SelectMenu,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  type VisitDto,
  type VisitStatus,
  currentCulture,
  resolveApiErrorMessage,
  settingsApi,
  visitsApi,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { Eye, History, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VisitDetailModal } from '../../components/visitors/VisitDetailModal';
import { formatDateTime } from '../../lib/format';
import { settingsKeys } from '../../lib/settingsKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { visitsKeys } from '../../lib/visitsKeys';

const PAGE_SIZE = 20;
const OPTIONS_PAGE_SIZE = 200;

/** Yerel gün başlangıcını UTC ISO'ya çevirir (§4 wire). Boş → undefined. */
function dayStartIso(date: string): string | undefined {
  if (!date) {
    return undefined;
  }
  const dt = new Date(`${date}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
}

/** Yerel gün sonunu UTC ISO'ya çevirir (aralık kapsayıcı olsun diye 23:59:59.999). */
function dayEndIso(date: string): string | undefined {
  if (!date) {
    return undefined;
  }
  const dt = new Date(`${date}T23:59:59.999`);
  return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
}

function visitorName(visit: VisitDto): string {
  return `${visit.visitor.firstName} ${visit.visitor.lastName}`.trim();
}

export function VisitHistoryPage() {
  const { t } = useTranslation();
  const culture = currentCulture();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [status, setStatus] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [detailVisit, setDetailVisit] = useState<VisitDto | null>(null);

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  // Saha + şube filtre seçenekleri (Ayarlar §10 uçları; kanonik seçim shape).
  const sitesQuery = useQuery({
    queryKey: settingsKeys.sites({
      page: 1,
      pageSize: OPTIONS_PAGE_SIZE,
      sortBy: 'name',
      sortDir: 'asc',
    }),
    queryFn: () =>
      settingsApi.listSites({
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    staleTime: 60_000,
  });
  const branchesQuery = useQuery({
    queryKey: settingsKeys.branchOptions(),
    queryFn: () =>
      settingsApi.listBranches({
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    staleTime: 60_000,
  });

  const siteOptions: SelectOption[] = useMemo(
    () => (sitesQuery.data?.items ?? []).map((s) => ({ value: s.uuid, label: s.name })),
    [sitesQuery.data],
  );
  const branchOptions: SelectOption[] = useMemo(
    () => (branchesQuery.data?.items ?? []).map((b) => ({ value: b.uuid, label: b.name })),
    [branchesQuery.data],
  );
  const statusOptions: SelectOption[] = [
    { value: 'OnSite', label: t('visitors.status.onSite') },
    { value: 'CheckedOut', label: t('visitors.status.checkedOut') },
  ];

  const params = {
    status: status.length > 0 ? (status as VisitStatus) : undefined,
    siteUuid: siteFilter.length > 0 ? siteFilter : undefined,
    branchUuid: branchFilter.length > 0 ? branchFilter : undefined,
    from: dayStartIso(from),
    to: dayEndIso(to),
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: visitsKeys.history(params),
    queryFn: () => visitsApi.listVisits(params),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(
    debouncedSearch || status || siteFilter || branchFilter || from || to,
  );
  function clearFilters() {
    setSearch('');
    setStatus('');
    setSiteFilter('');
    setBranchFilter('');
    setFrom('');
    setTo('');
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-5">
      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('visitors.history.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={resetPage(setSearch)}
            searchLabel={t('visitors.common.search')}
            searchPlaceholder={t('visitors.history.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <>
                {/* HER filtre `TableToolbarFilter`e AYRI AYRI sarmalı (S33 Slice 4 QA fix,
                    3. tur) — bkz. TableToolbar.tsx dosya başlığı. */}
                <TableToolbarFilter>
                  <SelectMenu
                    ariaLabel={t('visitors.history.filter.status')}
                    placeholder={t('visitors.history.filter.allStatuses')}
                    options={statusOptions}
                    value={status}
                    onValueChange={resetPage(setStatus)}
                  />
                </TableToolbarFilter>
                <TableToolbarFilter>
                  <SelectMenu
                    ariaLabel={t('visitors.history.filter.site')}
                    placeholder={t('visitors.history.filter.allSites')}
                    disabled={sitesQuery.isPending}
                    options={siteOptions}
                    value={siteFilter}
                    onValueChange={resetPage(setSiteFilter)}
                  />
                </TableToolbarFilter>
                <TableToolbarFilter>
                  <SelectMenu
                    ariaLabel={t('visitors.history.filter.branch')}
                    placeholder={t('visitors.history.filter.allBranches')}
                    disabled={branchesQuery.isPending}
                    options={branchOptions}
                    value={branchFilter}
                    onValueChange={resetPage(setBranchFilter)}
                  />
                </TableToolbarFilter>
                {/* Görünür etiket (a11y — coordinator bulgusu, 2. tur) — bkz. PermitsListPage. */}
                <TableToolbarFilter>
                  <Field
                    htmlFor="visit-history-filter-from"
                    label={t('visitors.history.filter.from')}
                  >
                    <Input
                      id="visit-history-filter-from"
                      type="date"
                      value={from}
                      onChange={(e) => resetPage(setFrom)(e.target.value)}
                    />
                  </Field>
                </TableToolbarFilter>
                <TableToolbarFilter>
                  <Field htmlFor="visit-history-filter-to" label={t('visitors.history.filter.to')}>
                    <Input
                      id="visit-history-filter-to"
                      type="date"
                      value={to}
                      onChange={(e) => resetPage(setTo)(e.target.value)}
                    />
                  </Field>
                </TableToolbarFilter>
              </>
            }
          />

          <Table caption={t('visitors.history.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('visitors.history.col.visitor')}</TableHead>
                <TableHead>{t('visitors.history.col.status')}</TableHead>
                <TableHead>{t('visitors.history.col.location')}</TableHead>
                <TableHead>{t('visitors.history.col.host')}</TableHead>
                <TableHead>{t('visitors.history.col.checkInAt')}</TableHead>
                <TableHead>{t('visitors.history.col.checkOutAt')}</TableHead>
                <TableHead className="text-right">{t('visitors.common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={7} />
              ) : query.isError ? (
                <TableMessageRow colSpan={7}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={7}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('visitors.history.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={History}
                      title={t('visitors.history.empty')}
                      description={t('visitors.history.empty.body')}
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((visit) => (
                  <TableRow key={visit.uuid}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-fg">{visitorName(visit)}</span>
                        <span className="text-xs text-muted">{visit.visitor.phone ?? ''}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Tag color={visit.status === 'OnSite' ? 'green' : 'gray'}>
                        {visit.status === 'OnSite'
                          ? t('visitors.status.onSite')
                          : t('visitors.status.checkedOut')}
                      </Tag>
                    </TableCell>
                    <TableCell className="text-muted">
                      {visit.siteName ?? visit.branchName ?? '—'}
                    </TableCell>
                    <TableCell className="text-muted">{visit.hostName ?? '—'}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted">
                      {formatDateTime(visit.checkInAt, culture)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted">
                      {formatDateTime(visit.checkOutAt, culture)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setDetailVisit(visit)}>
                          <Eye className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">{t('visitors.history.action.detail')}</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('visitors.pagination.nav'),
                previous: t('visitors.pagination.previous'),
                next: t('visitors.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <VisitDetailModal
        open={detailVisit !== null}
        onOpenChange={(open) => !open && setDetailVisit(null)}
        visit={detailVisit}
      />
    </div>
  );
}
